import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { 
    Button, 
    Box, 
    Typography, 
    CircularProgress,
    TextField,
    Divider,
    Checkbox,
    FormGroup,
    FormControlLabel
} from '@mui/material';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { Orders, Parts, Shippings } from '../../controllers';
import { SimpleValidators } from '../../utils';

export default function Main(){

    const [orderRef, setOrderRef] = React.useState('');
    
    const [loading, isLoading] = React.useState(false);

    const [data, setData] = React.useState(undefined);

    React.useEffect(() => {
        if( data === undefined){
            return;
        }

        isLoading(false);

    },[data]);

    const handleOrderInput = async(e) => {
        e.preventDefault();
        if(orderRef.trim().length <= 0) { return; }

        isLoading(true);
     
        const rInfo = await handleInfoLoad();
        if(rInfo === undefined) { await handleNotFound(); return; }

        const rParts = await handlePartsLoad(rInfo.id);
        if(rParts === undefined) { await handleNotFound(); return; }

        const rShip = await handleShipLoad(rInfo.id);
        if(rShip === undefined) { await handleNotFound(); return; }

        var onTimeResult = await calculateOrderOnTime(rInfo.etc, rShip.info?.humanDate);

        const result = {
            order: rInfo,
            parts: rParts,
            ship: rShip,
            onTime : onTimeResult
        };

        setData(result);
    }

    const handleNotFound = async() => {
        isLoading(false);
        setData(undefined);
    }

    const handleInfoLoad = async() => {
        let response = await Orders.GetByReference(orderRef);
        
        if(response === undefined){
            alert('Error while reading order information');
            return undefined;
        }

        if(!response.found){
            alert('No order found for Reference.\nPlease, check your input.');
            return undefined;
        }

        const dateInfo = new Date(response.result.date_create);
        const finalEtc = await handleWeekendCalculator(dateInfo);
        response.result.reception = new Date(dateInfo);
        response.result.etc = finalEtc;
        response.result.onTime = response.result.etc > new Date();

        return response.result;
    }

    const handleWeekendCalculator = async(start = new Date()) =>{
        var result = start;
        const finalA = new Date(start);
        const finalB = new Date(start);
        finalA.setDate(finalA.getDate() + 5);
        finalB.setDate(finalB.getDate() + 7);
        
        result = [1,2].includes(start.getDay()) ? finalA : finalB;

        return result;
    }

    const handlePartsLoad = async(id) => {
        let response = await Parts.GetDetailFullByOrder(id);
        
        if(response === undefined){
            alert('Error while reading order details');
            return undefined;
        }

        if(!response.success){
            alert(response.message);
            return undefined;
        }

        const min = await SimpleValidators.LeastPartStatus(response.result);
        const max = await SimpleValidators.HighestPartStatus(response.result);
        const hasReprints = await SimpleValidators.HasReprintedParts(response.result);

        response.result.minStatus = min;
        response.result.maxStatus = max;
        response.result.reprints = hasReprints;
        return response.result;
    }

    const handleShipLoad = async(id) => {
        const response = await Shippings.GetInfoByOrder(id);
        
        if(response === undefined){
            alert('Error while reading ship information');
            return undefined;
        }

        if (response.found) {
            response.result.humanDate = new Date(response.result.date_create);
        }

        const result = {
            found: response.found,
            info : response.result
        };

        return result;
    }

    const calculateOrderOnTime = async(orderDate, shipDate) => {
        var result = false;
        
        if (shipDate === undefined) {
            result = orderDate > new Date();
        } else{
            result = shipDate < orderDate;
        }

        return result;
    }

    return(
        <Box
            sx={{
                padding:2,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '50%',
                        left: '45%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Typography variant='h5'>
                        Loading Information
                    </Typography>
                    <CircularProgress />
                </Box>
            )}

            <Typography component="h1" variant="h5">
                Search Order by Reference
            </Typography>
            <Divider sx={{minWidth:'100%'}}></Divider>
            <Box 
                sx={{ mt: 1 }} 
                component='form'
                onSubmit={handleOrderInput}
            >
                <TextField
                    margin="normal"
                    fullWidth
                    label="Enter Order Reference"
                    placeholder='123ABC'
                    id="order"
                    name="order"
                    autoFocus
                    value={orderRef}
                    onChange={e => setOrderRef(e.target.value)}
                />
            </Box>

            {data != undefined && (
                <Box
                    sx={{
                        padding:2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography component="h1" variant="h6">
                        Information
                    </Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>

                    <Box
                        sx={{
                            mt:2,
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-evenly'
                        }}
                    >   
                        <TextField fullWidth label="Facility ID" value={data.order.id}/>
                        <TextField fullWidth label="Customer" value={data.order.data_general.case_client}/>
                        <TextField fullWidth label="Type" value={data.order.data_general.case_type}/>
                        <TextField fullWidth label="Facility Reception Date" value={data.order.reception.toDateString()}/>
                        <TextField fullWidth label="Estimated Time of Completion" value={data.order.etc.toDateString()}/>
                    </Box>

                    <Typography component="h1" variant="h6">
                        Status
                    </Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>

                    <Box
                        sx={{
                            mt:2,
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-evenly'
                        }}
                    >   
                        
                        <TextField fullWidth label="Least Stage" value={data.parts.minStatus.status}/>
                        <TextField fullWidth label="Highest Stage" value={data.parts.maxStatus.status}/>
                        <Box
                            sx={{
                                width:'100%',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 1,
                                borderColor: 'lightgray',
                                borderRadius: 1
                            }}
                        >
                            <Typography variant="button" sx={{ display: 'block' }}>
                                On Time :
                            </Typography>
                            <Checkbox checked={true} color={data.onTime ? 'primary' : 'error'} checkedIcon={data.onTime ? <CheckBoxIcon /> : <DisabledByDefaultIcon />} />
                        </Box>
                        <Box
                            sx={{
                                width:'100%',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 1,
                                borderColor: 'lightgray',
                                borderRadius: 1
                            }}
                        >
                            <Typography variant="button" sx={{ display: 'block' }}>
                                Has Reprints :
                            </Typography>
                            <Checkbox checked={data.parts.reprints}/>
                        </Box>
                    </Box>

                    <Typography component="h1" variant="h6">
                        Shipping
                    </Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>

                    <Box
                        sx={{
                            mt:2,
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-evenly'
                        }}
                    >   
                        <TextField fullWidth label="Tracking ID" value={data.ship.found ? data.ship.info.tracking : 'Not aviable yet'}/>
                        <TextField fullWidth label="Ship Date" value={data.ship.found ? data.ship.info.humanDate.toDateString() : 'Not aviable yet'}/>
                    </Box>

                </Box>
            )}
        </Box>
    );
}